import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import type { Partner } from "@crm/shared";
import styles from "./PartnersPage.module.css";
import { Button } from "../../design/Button.js";
import { Card } from "../../design/Card.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { SearchInput } from "../../design/SearchInput.js";
import { Segmented, type SegmentedOption } from "../../design/Segmented.js";
import { EmptyRow, Table, Td, Th, tableStyles } from "../../design/Table.js";
import { usePageChrome } from "../../design/pageChrome.js";
import { formatCount } from "../../format/money.js";
import { DeleteDialog } from "../../forms/DeleteDialog.js";
import { PartnerSwatch } from "../../components/PartnerSwatch.js";
import { usePartnerMutations } from "../../api/mutations.js";
import { useHasRole } from "../../auth/useAuth.js";
import { PartnerFormDialog } from "./PartnerFormDialog.js";
import { DEFAULT_PARTNERS_QUERY, usePartners, type PartnersQueryState } from "../../api/queries.js";

/** Which dialog is open, and about what. One state, so two can't be open at once. */
type PartnerAction =
  | { kind: "create" }
  | { kind: "edit"; partner: Partner }
  | { kind: "delete"; partner: Partner };

/**
 * Two readings of the same table. "Top level only" is the `depth=0` filter, and
 * it is the view for "who are our relationships" — an owned company is part of
 * one, not another one.
 */
const SCOPES: ReadonlyArray<SegmentedOption<string>> = [
  { value: "all", label: "All partners" },
  { value: "top", label: "Top level only", title: "Only partners nobody else owns" },
];

/**
 * Why the table is empty, in the terms of whatever emptied it. "No partners
 * match" over a `depth=0` filter usually means the owned companies are all
 * that exist — which the reader cannot see, because they are the rows the
 * filter just removed.
 */
function emptyDescription(state: PartnersQueryState): string {
  if (state.q !== "") {
    return `Nothing matches “${state.q}”. Partner search covers the name and the notes.`;
  }
  if (state.topLevelOnly) {
    return "No partner is at the top of a family. Switch to all partners to see the ones that are owned by somebody.";
  }
  return "Seed the database (npm run seed) to get partners, agreements and their term timelines — or create the first one.";
}

export function PartnersPage(): ReactNode {
  const navigate = useNavigate();
  const [state, setState] = useState<PartnersQueryState>(DEFAULT_PARTNERS_QUERY);
  const [action, setAction] = useState<PartnerAction | null>(null);
  const query = usePartners(state);
  const mutations = usePartnerMutations();
  const canEdit = useHasRole("crm_editor");

  usePageChrome({
    title: "Partners",
  });

  const columns = canEdit ? 5 : 4;

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <SearchInput
          value={state.q}
          onCommit={(q) => setState((previous) => ({ ...previous, q, page: 1 }))}
          placeholder="Search partner name or notes"
          ariaLabel="Search partners"
        />
        <Segmented
          label="Partner scope"
          options={SCOPES}
          value={state.topLevelOnly ? "top" : "all"}
          // A different filter is a different result set, so page 1 is the only
          // page that still means anything.
          onChange={(scope) =>
            setState((previous) => ({ ...previous, topLevelOnly: scope === "top", page: 1 }))
          }
        />
        <span className={styles.count}>
          {query.data === undefined ? "" : `${formatCount(query.data.total)} partners`}
        </span>
        {canEdit && (
          <Button variant="primary" onClick={() => setAction({ kind: "create" })}>
            {/* The glyph is decoration, so the button's accessible name stays
                the words alone rather than "plus New partner". */}
            <span aria-hidden="true">+</span>
            New partner
          </Button>
        )}
      </div>

      <QueryBoundary query={query} label="the partner list">
        {(page) => (
          <Card>
            <Table label="Partners">
              <thead>
                <tr>
                  <Th>Partner</Th>
                  <Th>Owned by</Th>
                  <Th numeric>Owned companies</Th>
                  <Th>Notes</Th>
                  {canEdit && <Th />}
                </tr>
              </thead>
              <tbody>
                {page.items.length === 0 ? (
                  <EmptyRow
                    colSpan={columns}
                    title="No partners match"
                    description={emptyDescription(state)}
                  />
                ) : (
                  page.items.map((partner) => (
                    <tr
                      key={partner.id}
                      className={tableStyles.rowLink}
                      onClick={() =>
                        void navigate({
                          to: "/partners/$partnerId",
                          params: { partnerId: partner.id },
                        })
                      }
                    >
                      <Td>
                        {/* A real link inside the clickable row, because a row
                            is not reachable by keyboard and "open the partner"
                            is this screen's whole purpose. */}
                        <Link
                          to="/partners/$partnerId"
                          params={{ partnerId: partner.id }}
                          className={styles.name}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <PartnerSwatch color={partner.color} />
                          {partner.name}
                        </Link>
                      </Td>
                      <Td muted={partner.parent_id === null}>
                        {partner.parent_id === null || partner.parent_name === null ? (
                          "—"
                        ) : (
                          <Link
                            to="/partners/$partnerId"
                            params={{ partnerId: partner.parent_id }}
                            onClick={(event) => event.stopPropagation()}
                          >
                            {partner.parent_name}
                          </Link>
                        )}
                      </Td>
                      {/* Direct children only — the count on the record is not
                          the size of the subtree, and labelling it as one would
                          make a two-level family look flat. */}
                      <Td numeric mono muted={partner.child_count === 0}>
                        {partner.child_count === 0 ? "—" : formatCount(partner.child_count)}
                      </Td>
                      <Td muted={partner.notes === null}>{partner.notes ?? "—"}</Td>
                      {canEdit && (
                        <Td onClick={(event) => event.stopPropagation()}>
                          <div className={styles.rowActions}>
                            <Button
                              small
                              variant="subtle"
                              aria-label={`Edit ${partner.name}`}
                              onClick={() => setAction({ kind: "edit", partner })}
                            >
                              Edit
                            </Button>
                            <Button
                              small
                              variant="subtle"
                              aria-label={`Delete ${partner.name}`}
                              onClick={() => setAction({ kind: "delete", partner })}
                            >
                              Delete
                            </Button>
                          </div>
                        </Td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>

            {page.items.length > 0 && (
              <div className={styles.pager}>
                <Button
                  small
                  disabled={state.page <= 1}
                  onClick={() => setState((previous) => ({ ...previous, page: previous.page - 1 }))}
                >
                  Previous
                </Button>
                <Button
                  small
                  disabled={page.offset + page.limit >= page.total}
                  onClick={() => setState((previous) => ({ ...previous, page: previous.page + 1 }))}
                >
                  Next
                </Button>
              </div>
            )}
          </Card>
        )}
      </QueryBoundary>

      {action?.kind === "create" && <PartnerFormDialog onClose={() => setAction(null)} />}
      {action?.kind === "edit" && (
        <PartnerFormDialog partner={action.partner} onClose={() => setAction(null)} />
      )}
      {action?.kind === "delete" && (
        <DeleteDialog
          open
          onClose={() => setAction(null)}
          title="Delete partner"
          description={`${action.partner.name} will be removed. Its agreements, their terms and every deal underneath them go with it — if that isn't what you want, move the deals first.`}
          record={action.partner}
          remove={(id, version) => mutations.remove.mutateAsync({ id, version })}
          fetchCurrent={mutations.fetchCurrent}
          onDeleted={() => setAction(null)}
        />
      )}
    </div>
  );
}
